import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Users, Package, Inbox, Clock, Check, X, Store, Building2, UserCog } from "lucide-react";
import { Link } from "react-router-dom";
import AdminExportApprovals from "../components/AdminExportApprovals";

const StatCard = ({ icon: Icon, label, value, testid, sub }) => (
  <div className="bg-white border border-[#7AB2B2] rounded-xl p-5 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 shrink-0 rounded-lg bg-[#EBF4F6] flex items-center justify-center text-[#09637E]">
        <Icon size={20} />
      </div>
      <div className="text-sm text-[#666666] font-bold">{label}</div>
    </div>
    <div className="text-3xl font-extrabold text-[#09637E] tabular-nums">{value ?? "-"}</div>
    {sub && <div className="text-xs text-[#666666] mt-1 tabular-nums" data-testid={`${testid}-sub`}>{sub}</div>}
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, o] = await Promise.all([api.get("/stats/admin"), api.get("/admin/pending"), api.get("/admin/org-overview").catch(() => ({ data: null }))]);
      setStats(s.data);
      setPending(p.data);
      setOrg(o.data?.totals || null);
    } catch (err) {
      toast.error(arabicError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/approve/${id}`); toast.success("تمت الموافقة"); load(); } catch (e) { toast.error(arabicError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/reject/${id}`); toast.success("تم الرفض"); load(); } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">لوحة الإدارة</h1>
        <p className="text-[#666666] mt-1">إدارة التجار والصور والحجوزات</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} label="طلبات معلقة" value={stats?.pending_merchants} testid="stat-pending" />
        <StatCard icon={Users} label="تجار معتمدون" value={stats?.approved_merchants} testid="stat-approved" />
        <StatCard icon={Package} label="إجمالي الصور" value={stats?.total_photos} testid="stat-photos" />
        <StatCard icon={Inbox} label="إجمالي الحجوزات" value={stats?.total_reservations} testid="stat-reservations" />
        <StatCard icon={Store} label="إجمالي التجار" value={org?.merchants} testid="stat-total-merchants" />
        <StatCard icon={Building2} label="إجمالي الفروع" value={org?.branches} testid="stat-total-branches"
          sub={org && `${org.main_branches} رئيسي · ${org.additional_branches} إضافي`} />
        <StatCard icon={UserCog} label="إجمالي الموظفين" value={org?.employees} testid="stat-total-employees" />
      </div>

      <AdminExportApprovals />

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-extrabold text-[#333333]">طلبات التسجيل المعلقة</h2>
          <Link to="/admin/merchants" className="text-[#09637E] font-bold hover:underline text-sm">عرض التجار المعتمدين ←</Link>
        </div>

        {loading ? (
          <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
        ) : pending.length === 0 ? (
          <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center" data-testid="empty-pending">
            <Users className="mx-auto text-[#7AB2B2] mb-3" size={48} />
            <p className="text-[#666666] text-lg">لا توجد طلبات معلقة حالياً</p>
          </div>
        ) : (
          <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
            <div className="md:hidden bg-[#EBF4F6] text-[#09637E] text-xs font-bold px-3 py-2 border-b border-[#7AB2B2]" data-testid="table-swipe-hint">← اسحب الجدول يميناً ويساراً لعرض بقية الأعمدة والإجراءات</div>
            <div className="overflow-x-auto">
              <table className="w-full text-right">
                <thead className="bg-[#EBF4F6] text-[#333333]">
                  <tr>
                    <th className="p-4 text-sm font-bold">البريد الإلكتروني</th>
                    <th className="p-4 text-sm font-bold">الاسم الكامل</th>
                    <th className="p-4 text-sm font-bold">اسم المحل</th>
                    <th className="p-4 text-sm font-bold">المحافظة</th>
                    <th className="p-4 text-sm font-bold">الهاتف</th>
                    <th className="p-4 text-sm font-bold">المواد</th>
                    <th className="p-4 text-sm font-bold">تاريخ التسجيل</th>
                    <th className="p-4 text-sm font-bold">الإجراء</th>
                  </tr>
                </thead>
                <tbody data-testid="pending-table">
                  {pending.map((m) => (
                    <tr key={m.id} className="border-t border-[#EBF4F6]" data-testid={`pending-row-${m.id}`}>
                      <td className="p-4 text-[#333333]">{m.email}</td>
                      <td className="p-4 text-[#333333]">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "-"}</td>
                      <td className="p-4 font-bold text-[#333333]">{m.store_name}</td>
                      <td className="p-4 text-[#666666]">{m.city}</td>
                      <td className="p-4 text-[#666666] text-sm" dir="ltr">{m.phone_number || "-"}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {(m.material_types || []).map((mt) => (
                            <span key={mt} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EBF4F6] text-[#09637E]">{mt}</span>
                          ))}
                          {(!m.material_types || m.material_types.length === 0) && <span className="text-xs text-[#666666]">-</span>}
                        </div>
                      </td>
                      <td className="p-4 text-[#666666] text-sm">{new Date(m.created_at).toLocaleDateString("ar-SY")}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <button onClick={() => approve(m.id)} data-testid={`approve-btn-${m.id}`}
                            className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 whitespace-nowrap">
                            <Check size={16} /> موافقة
                          </button>
                          <button onClick={() => reject(m.id)} data-testid={`reject-btn-${m.id}`}
                            className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1 whitespace-nowrap">
                            <X size={16} /> رفض
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDashboard;
